import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  Table, 
  Button, 
  Input, 
  FormGroup, 
  Label, 
  Modal, 
  ModalHeader, 
  ModalBody,
  ModalFooter,
  Badge,
  Spinner
} from "reactstrap";
import { AdminService } from "../../services/AdminService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { format } from "date-fns";

interface JotForm {
  _id: string;
  title_form: string;
  form_id: string;
  status: "ENABLED" | "DISABLED";
  createdAt: string;
  updatedAt: string;
}

const JotFormManagement: React.FC = () => {
  const [jotforms, setJotforms] = useState<JotForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "ENABLED" | "DISABLED">("all");
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingForm, setEditingForm] = useState<JotForm | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    title_form: "",
    form_id: "",
    status: "ENABLED" as "ENABLED" | "DISABLED"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchJotforms();
  }, []);

  const fetchJotforms = async () => {
    try {
      setIsLoading(true);
      // Note: We'll need to create these API endpoints in AdminService
      const response = await AdminService.getAllJotforms();
      if (response.success) {
        setJotforms(response.data || []);
      } else {
        toast.error("Failed to fetch JotForms");
      }
    } catch (error) {
      console.error("Error fetching JotForms:", error);
      toast.error("Failed to fetch JotForms");
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredJotforms = () => {
    return jotforms.filter(form => {
      const matchesSearch = 
        form.title_form.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.form_id.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || form.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const openCreateModal = () => {
    setModalMode("create");
    setFormData({
      title_form: "",
      form_id: "",
      status: "ENABLED"
    });
    setEditingForm(null);
    setShowModal(true);
  };

  const openEditModal = (form: JotForm) => {
    setModalMode("edit");
    setFormData({
      title_form: form.title_form,
      form_id: form.form_id,
      status: form.status
    });
    setEditingForm(form);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      title_form: "",
      form_id: "",
      status: "ENABLED"
    });
    setEditingForm(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (modalMode === "create") {
        const response = await AdminService.createJotform(formData);
        if (response.success) {
          toast.success("JotForm created successfully");
          fetchJotforms();
          closeModal();
        } else {
          toast.error(response.message || "Failed to create JotForm");
        }
      } else {
        const response = await AdminService.updateJotform(editingForm!._id, formData);
        if (response.success) {
          toast.success("JotForm updated successfully");
          fetchJotforms();
          closeModal();
        } else {
          toast.error(response.message || "Failed to update JotForm");
        }
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (form: JotForm) => {
    const result = await Swal.fire({
      title: 'Delete JotForm?',
      text: `Are you sure you want to delete "${form.title_form}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await AdminService.deleteJotform(form._id);
        if (response.success) {
          toast.success("JotForm deleted successfully");
          fetchJotforms();
        } else {
          toast.error(response.message || "Failed to delete JotForm");
        }
      } catch (error: any) {
        console.error("Error deleting JotForm:", error);
        toast.error(error.message || "Failed to delete JotForm");
      }
    }
  };

  const toggleStatus = async (form: JotForm) => {
    const newStatus = form.status === "ENABLED" ? "DISABLED" : "ENABLED";
    
    setLoadingStates(prev => ({ ...prev, [form._id]: true }));
    
    try {
      const response = await AdminService.updateJotform(form._id, {
        ...form,
        status: newStatus
      });
      
      if (response.success) {
        toast.success(`JotForm ${newStatus.toLowerCase()} successfully`);
        fetchJotforms();
      } else {
        toast.error(response.message || "Failed to update status");
      }
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error(error.message || "Failed to update status");
    } finally {
      setLoadingStates(prev => ({ ...prev, [form._id]: false }));
    }
  };

  const getStatusBadge = (status: string) => {
    return status === "ENABLED" 
      ? <Badge color="success">Enabled</Badge>
      : <Badge color="secondary">Disabled</Badge>;
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <Spinner color="primary" size="lg" />
            <p className="mt-2">Loading JotForms...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Jot Form" breadcrumbItem="Management" />
        
        <Row>
          <Col>
            <Card>
              <CardBody>
                {/* Header with search and create button */}
                <Row className="mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label>Search JotForms</Label>
                      <Input
                        type="text"
                        placeholder="Search by title or form ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Filter by Status</Label>
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                      >
                        <option value="all">All</option>
                        <option value="ENABLED">Enabled</option>
                        <option value="DISABLED">Disabled</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={5}>
                    <FormGroup>
                      <Label>&nbsp;</Label>
                      <div className="d-flex gap-2">
                        <Button 
                          color="primary" 
                          onClick={openCreateModal}
                        >
                          <i className="mdi mdi-plus me-1"></i>
                          Add New JotForm
                        </Button>
                        <Button 
                          color="info" 
                          onClick={fetchJotforms}
                        >
                          <i className="mdi mdi-refresh me-1"></i>
                          Refresh
                        </Button>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                {/* JotForms Table */}
                <div className="table-responsive">
                  <Table className="table table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Form ID</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Updated At</th>
                        <th style={{ width: '180px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredJotforms().map(form => (
                        <tr key={form._id}>
                          <td>{form.title_form}</td>
                          <td><code>{form.form_id}</code></td>
                          <td>{getStatusBadge(form.status)}</td>
                          <td>
                            {form.createdAt 
                              ? format(new Date(form.createdAt), 'MMM dd, yyyy HH:mm')
                              : '-'
                            }
                          </td>
                          <td>
                            {form.updatedAt 
                              ? format(new Date(form.updatedAt), 'MMM dd, yyyy HH:mm')
                              : '-'
                            }
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => openEditModal(form)}
                              className="me-1"
                            >
                              <i className="mdi mdi-pencil"></i>
                            </Button>
                            <Button
                              color={form.status === "ENABLED" ? "warning" : "success"}
                              size="sm"
                              onClick={() => toggleStatus(form)}
                              className="me-1"
                              disabled={loadingStates[form._id]}
                            >
                              {loadingStates[form._id] ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className={`mdi mdi-${form.status === "ENABLED" ? "pause" : "play"}`}></i>
                              )}
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(form)}
                            >
                              <i className="mdi mdi-delete"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {getFilteredJotforms().length === 0 && (
                  <div className="text-center py-4">
                    <i className="mdi mdi-format-list-bulleted display-4 text-muted"></i>
                    <p className="text-muted mt-2">No JotForms found</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Create/Edit Modal */}
        <Modal isOpen={showModal} toggle={closeModal}>
          <form onSubmit={handleSubmit}>
            <ModalHeader toggle={closeModal}>
              {modalMode === "create" ? "Add New JotForm" : "Edit JotForm"}
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label for="title_form">Form Title <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="title_form"
                  value={formData.title_form}
                  onChange={(e) => setFormData({...formData, title_form: e.target.value})}
                  placeholder="Enter form title..."
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label for="form_id">JotForm ID <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="form_id"
                  value={formData.form_id}
                  onChange={(e) => setFormData({...formData, form_id: e.target.value})}
                  placeholder="Enter JotForm ID (e.g., 240585456789123456)"
                  required
                />
                <small className="text-muted">
                  You can find the Form ID in the JotForm URL or settings
                </small>
              </FormGroup>
              
              {modalMode === "edit" && (
                <FormGroup>
                  <Label for="status">Status</Label>
                  <Input
                    type="select"
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as "ENABLED" | "DISABLED"})}
                  >
                    <option value="ENABLED">Enabled</option>
                    <option value="DISABLED">Disabled</option>
                  </Input>
                  <small className="text-muted">
                    Only enabled forms will appear in the therapist dropdown
                  </small>
                </FormGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button 
                color="primary" 
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    {modalMode === "create" ? "Creating..." : "Updating..."}
                  </>
                ) : (
                  modalMode === "create" ? "Create JotForm" : "Update JotForm"
                )}
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </Container>
    </div>
  );
};

export default JotFormManagement;